/** Context + hooks for the Ads Manager filter — separate from the
 *  provider component so Fast Refresh keeps working (a file that exports
 *  both components and functions loses it). */

import { createContext, useContext } from "react";
import type { AdsManagerFilter, FilterCounts, FilterTab } from "@/lib/ads-manager-filter";

export type FilterGrain = "campaigns" | "ad_groups" | "keywords";

export type AdsFilterCtx = {
  grain: FilterGrain;
  applied: AdsManagerFilter;
  counts: FilterCounts;
  isActive: boolean;
  /** `?filter=` for the stats hooks; undefined when nothing applies. */
  apiParam: string | undefined;
  openModal: (tab: FilterTab) => void;
  removeType: (tab: FilterTab) => void;
  reset: () => void;
  describe: (tab: FilterTab) => string;
  /** Labels the current level's entities carry (`Label.applied_to`), for
   *  the client-side check of rows the server never saw. */
  labelIdsOf: (entityId: number) => ReadonlySet<number>;
  /** «Split by devices»: the app has an iPad build (else no menu item). */
  splitAvailable: boolean;
  /** The split is on for this account × app (only when available). */
  splitOn: boolean;
  setSplit: (on: boolean) => void;
  /** A chip row is needed: some filter applies or the split is on. */
  showChips: boolean;
};

export const AdsFilterContext = createContext<AdsFilterCtx | null>(null);

export function useAdsFilter(): AdsFilterCtx {
  const ctx = useContext(AdsFilterContext);
  if (!ctx) throw new Error("useAdsFilter outside AdsFilterProvider");
  return ctx;
}

export function useAdsFilterOptional(): AdsFilterCtx | null {
  return useContext(AdsFilterContext);
}


/** Props for the «Split by devices» chip, or undefined when it's off.
 *  `mutedReason` — the tab can't split (Ads): the chip stays, dimmed. */
export function splitChip(
  ctx: AdsFilterCtx | null | undefined,
  mutedReason?: string,
): { onRemove: () => void; mutedReason?: string } | undefined {
  if (!ctx?.splitOn) return undefined;
  return { onRemove: () => ctx.setSplit(false), mutedReason };
}
